import { FC, useCallback, useState } from 'react';
import { Image, MenuProps, Space, Typography, Dropdown } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import bell_white from 'assets/images/bell_white.svg';
import {
  NotificationRow,
  markNotificationRead,
  removeNotification
} from 'services/api/notifications';

import {
  ActionButton,
  IconContainer,
  Item,
  ItemContainer,
  MenuContainer,
  StyledHeader
} from './styled';

const { Title, Text } = Typography;

interface IProps {
  item: NotificationRow;
  onClick: () => void;
  refresh: () => void;
}

const NotificationItem: FC<IProps> = ({ item, onClick, refresh }) => {
  const { t } = useTranslation();

  const [deleted, setDeleted] = useState(false);

  const handleRead = useCallback(() => {
    markNotificationRead(item.id).then(() => refresh());
  }, [item.id, refresh]);

  const onClickRead = useCallback(() => {
    onClick();
    handleRead();
  }, [onClick, handleRead]);

  const onClickRemove = useCallback(() => {
    setDeleted(true);

    removeNotification(item.id).then(() => refresh());
  }, [item.id, refresh]);

  const active = !item?.read;

  const items: MenuProps['items'] = [
    ...(active
      ? [
          {
            label: (
              <ActionButton data-id="action-button">
                {t('notifications.mark_read')}
              </ActionButton>
            ),
            key: 'read',
            onClick: handleRead
          }
        ]
      : []),
    {
      label: (
        <ActionButton data-id="action-button">
          {t('notifications.delete')}
        </ActionButton>
      ),
      key: 'delete',
      onClick: onClickRemove
    }
  ];

  if (!item) {
    return null;
  }

  return (
    <ItemContainer deleted={deleted}>
      <Link
        to={`/appointments/details/${item.body?.appointmentId}`}
        onClick={onClickRead}
        className="notification-item-link"
      >
        <Item active={active}>
          <div>
            <IconContainer active={active}>
              <Image src={bell_white} width={16} preview={false} />
            </IconContainer>
          </div>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <StyledHeader>
              <Title level={5} style={{ marginBottom: 0 }}>
                {item.body.title}
              </Title>
            </StyledHeader>
            <Text>{item.body.body}</Text>
            <Text style={{ color: `${active ? '#4385F5' : '#9CA3BF'}` }}>
              {dayjs(item.createdAt).format(FULL_DATE_FORMAT)}
            </Text>
          </Space>
        </Item>
      </Link>
      <Dropdown menu={{ items }} placement="bottomRight" trigger={['click']}>
        <MenuContainer>
          <EllipsisOutlined color="#6B7083" size={32} />
        </MenuContainer>
      </Dropdown>
    </ItemContainer>
  );
};

export default NotificationItem;
